import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  COMPANY_DATA,
  COMPANY_ID_KEY,
  COMPANY_PRICES,
  Company,
  ICompany,
  IInternalVendor,
  IUser,
} from '@tacliatech/types';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { StorageService } from '../storage/storage.service';
import { AssociatedCompany } from '@web-frontend/shared/components/sidebar/sidebar';
import { AddCompanyModalComponent } from '@web-frontend/shared/components/add-company-modal/add-company-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastService } from '../toast/toast.service';
import { ObjectId } from '@web-frontend/shared/utils/isValidObjectId.util';
import { decodeToken } from 'src/types-legacy/lib/utils/jwt.utils';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  refreshCompanyCounter$ = new BehaviorSubject<Company.CountUsers>({
    companyUsers: 0,
  });

  private _cleared = false;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private toastService: ToastService
  ) {}

  init(): void {
    this._cleared = false;
    this.companies;
  }

  public get companies(): AssociatedCompany[] {
    if (this._cleared) return [];
    try {
      const token = StorageService.Token;
      if (!token) throw new Error('Token not found');
      const { associatedCompanies } = decodeToken(token);

      return associatedCompanies;
    } catch (error) {
      return [];
    }
  }

  addCompany(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const dialogRef = this.dialog.open(AddCompanyModalComponent);

      dialogRef.afterClosed().subscribe(async (form) => {
        if (form) {
          const newId = ObjectId();
          const companyData = {
            id: newId,
            name: form.name,
            country: form.country,
          };

          await this.createCompany(companyData)
            .toPromise()
            .then(() => {
              this.init(); // Refresh companies list
              resolve(newId);
            })
            .catch(() => {
              this.toastService.show({
                text: 'header.menu.errorAddCompany',
                type: 'error',
              });
              reject(null);
            });
        }
      });
    });
  }

  createCompany(body: {
    id: string;
    name: string;
    country: string;
  }): Observable<Company.Output> {
    return this.http.post<Company.Output>(`:API_URL/companies`, body);
  }

  findOne(id: string): Observable<ICompany> {
    return this.http.get<ICompany>(`:API_URL/company/${id}`).pipe(
      tap((res) => {
        const personalization = res?.personalization || {};
        const modules =
          res?.modules
            ?.filter((module) => module.active)
            ?.map((module) => module.id) || [];
        const entitlements = res?.subscription?.entitlements || [];
        const plans = res?.subscription?.item_price_ids || [];
        const companyData = {
          name: res.name,
          customer: res.customer._id,
          created_at: res?.created_at,
          modules,
          entitlements,
          plans,
          lastPlanId: res?.subscription?.last_item_price_ids,
          locationCountry: res?.location?.country || 'ES',
          sector: res?.customer?.sector,
          stChoice: res.stChoice,
          company_utm_campaign: res.utm_data?.[0]?.utm_campaign ?? null,
          prices: res.subscription?.prices ?? null,
          personalization,
        };

        const existingData = JSON.parse(StorageService.companyData ?? '{}');
        StorageService.SetItem(COMPANY_DATA, {
          ...existingData,
          ...companyData,
        });

        StorageService.SetItem(
          COMPANY_PRICES,
          companyData?.prices?.map((price) => ({
            periodUnit: price.period_unit,
            price: price.price,
            itemPriceId: price.item_price_id,
          }))
        );
      })
    );
  }

  findOneAndUpdate(
    id: string,
    value: { [key: string]: any }
  ): Observable<Company.Output> {
    return this.http.put<Company.Output>(`:API_URL/company/${id}`, {
      ...value,
    });
  }

  findUsersByCompany(id: string): Observable<IUser[]> {
    return this.http.get<IUser[]>(`:API_URL/users/company/${id}`);
  }

  findCountUsersByCompany(id: string): Observable<Company.CountUsers> {
    return this.http.get<Company.CountUsers>(`:API_URL/company/${id}/users`);
  }

  findInternalVendors(idCompany: string): Observable<IInternalVendor[]> {
    return this.http.get<IInternalVendor[]>(
      `:API_URL/company/${idCompany}/internal-vendors`
    );
  }

  delete(id: string) {
    return this.http.delete(`:API_URL/company/${id}`);
  }

  getCompanyInfo(): Observable<ICompany> {
    return this.findOne(StorageService.GetItem(COMPANY_ID_KEY));
  }

  clearCompanyCache() {
    this._cleared = true;
  }
}
